/**
 * Navigation Configuration for Permission-based UI
 *
 * Defines the navigation structure with feature permission requirements
 * and logical grouping for the dashboard sidebar.
 */

import {
  FeaturePermission,
  FEATURE_PERMISSIONS,
} from "../constants/featurePermissions";

// Navigation item interface
export interface NavigationItem {
  id: string;
  name: string;
  href: string;
  icon: string;
  description?: string;
  requiredFeatures: FeaturePermission[];
  requireAll?: boolean; // If true, user must have ALL features. If false, user needs ANY feature.
  badge?: string; // Optional badge text
  isNew?: boolean; // Mark as new feature
  order: number; // Display order
  showLock?: boolean; // Show lock icon when subscription limits are reached
  hideFromSidebar?: boolean; // If true, item appears in search but not in sidebar navigation
  activeFor?: string[]; // Additional route patterns that should highlight this nav item (e.g., ["/rental-units"] for properties)
}

// Navigation group interface
export interface NavigationGroup {
  id: string;
  name: string;
  icon: string;
  description: string;
  items: NavigationItem[];
  order: number;
  collapsible?: boolean;
  defaultExpanded?: boolean;
}

// Core navigation items that are always visible
export const CORE_NAVIGATION: NavigationItem[] = [
  {
    id: "dashboard",
    name: "Dashboard",
    href: "",
    icon: "🏠",
    description: "Overview and quick actions",
    requiredFeatures: [], // Always visible
    requireAll: false,
    order: 1,
  },
  {
    id: "profile",
    name: "Profile",
    href: "/profile",
    icon: "👤",
    description: "User profile and settings",
    requiredFeatures: [], // Always visible
    requireAll: false,
    order: 2,
  },
];

// Property management navigation group
const PROPERTY_MANAGEMENT_GROUP: NavigationGroup = {
  id: "property_management",
  name: "Property Management",
  icon: "🏢",
  description: "Manage properties and rental units",
  order: 1,
  collapsible: true,
  defaultExpanded: true,
  items: [
    {
      id: "properties",
      name: "Properties",
      href: "/properties",
      icon: "🏠",
      description: "Manage properties and buildings",
      requiredFeatures: [FEATURE_PERMISSIONS.PROPERTIES_READ],
      requireAll: false,
      order: 1,
      showLock: true, // Show lock when limit reached
      activeFor: ["/rental-units", "/meter-readings"], // Also highlight when on rental-units routes
    },
    {
      id: "rental_units",
      name: "Rental Units",
      href: "/rental-units",
      icon: "🏘️",
      description: "Manage rental units across all properties",
      requiredFeatures: [FEATURE_PERMISSIONS.RENTALUNIT_READ],
      requireAll: false,
      order: 2,
      showLock: true,
      hideFromSidebar: true, // Hidden from sidebar but accessible via search and route
    },
    // {
    //   id: "meter_readings",
    //   name: "Meter Readings",
    //   href: "/meter-readings",
    //   icon: "gages",
    //   description: "Manage meter readings",
    //   requiredFeatures: [],
    //   requireAll: false,
    //   order: 3,
    //   showLock: true,
    //   hideFromSidebar: true, 
    // },
    {
      id: "virtual_tours",
      name: "Virtual Tours",
      href: "/virtual-tours",
      icon: "🥽",
      description: "Property virtual tours",
      requiredFeatures: [FEATURE_PERMISSIONS.ADMIN_READ],
      requireAll: true,
      badge: "Dev",
      order: 3,
    },
    {
      id: "map_demo",
      name: "Property Map",
      href: "/map-demo",
      icon: "🗺️",
      description: "Interactive property map",
      requiredFeatures: [FEATURE_PERMISSIONS.ADMIN_READ],
      requireAll: true,
      badge: "Dev",
      order: 4,
    },
  ],
};

// Tenant management navigation group
const TENANT_MANAGEMENT_GROUP: NavigationGroup = {
  id: "tenant_management",
  name: "Tenant Management",
  icon: "👥",
  description: "Manage tenants and bookings",
  order: 2,
  collapsible: true,
  defaultExpanded: true,
  items: [
    {
      id: "tenants",
      name: "Tenants",
      href: "/tenants",
      icon: "👥",
      description: "Manage tenant information",
      requiredFeatures: [FEATURE_PERMISSIONS.TENANTS_READ],
      requireAll: false,
      order: 1,
      showLock: true,
    },
  ],
};

// Financial management navigation group
const FINANCIAL_MANAGEMENT_GROUP: NavigationGroup = {
  id: "financial_management",
  name: "Financial Management",
  icon: "💰",
  description: "Payments, invoices, and financial tracking",
  order: 3,
  collapsible: true,
  defaultExpanded: true,
  items: [
    {
      id: "invoices",
      name: "Invoices",
      href: "/invoices",
      icon: "🧾",
      description: "Invoice management and tracking",
      requiredFeatures: [
        FEATURE_PERMISSIONS.INVOICES_READ,
      ],
      requireAll: true,
      order: 1,
      showLock: true,
    },
    {
      id: "payments",
      name: "Payments",
      href: "/payments",
      icon: "💳",
      description: "Payment tracking and management",
      requiredFeatures: [
        FEATURE_PERMISSIONS.PAYMENTS_READ,
      ],
      requireAll: true,
      order: 2,
      showLock: true,
      activeFor: ["/payment-tracking", "/payment-gateway"], // Also highlight when on payment sub-routes
    },
    {
      id: "expenses",
      name: "Expenses",
      href: "/expenses",
      icon: "💳",
      description: "Expense tracking and management",
      requiredFeatures: [
        FEATURE_PERMISSIONS.EXPENSES_READ,
      ],
      requireAll: true,
      order: 2,
      showLock: true,
    },
  ],
};

// Maintenance management navigation group
const MAINTENANCE_GROUP: NavigationGroup = {
  id: "maintenance",
  name: "Maintenance",
  icon: "🔧",
  description: "Maintenance requests and assignments",
  order: 4,
  collapsible: true,
  defaultExpanded: true,
  items: [
    {
      id: "maintenance",
      name: "Maintenance",
      href: "/maintenance",
      icon: "🔧",
      description: "Maintenance requests and workflow",
      requiredFeatures: [FEATURE_PERMISSIONS.MAINTENANCE_READ],
      requireAll: false,
      order: 1,
      showLock: true,
    },
    {
      id: "inventory",
      name: "Inventory",
      href: "/maintenance/inventory",
      icon: "📦",
      description: "Manage maintenance inventory, stock, and categories",
      requiredFeatures: [FEATURE_PERMISSIONS.INVENTORY_ITEMS_READ],
      requireAll: false,
      order: 2,
      hideFromSidebar: true,
      activeFor: ["/maintenance/inventory"],
    },
  ],
};

// Analytics and reporting navigation group
const ANALYTICS_GROUP: NavigationGroup = {
  id: "analytics",
  name: "Analytics & Reports",
  icon: "📊",
  description: "Business analytics and reporting",
  order: 5,
  collapsible: true,
  defaultExpanded: false,
  items: [
    {
      id: "analytics",
      name: "Analytics",
      href: "/analytics",
      icon: "📊",
      description: "Business analytics and insights",
      requiredFeatures: [FEATURE_PERMISSIONS.ANALYTICS_READ],
      requireAll: false,
      order: 1,
      showLock: true, // Show lock when limit reached
    },
  ],
};

// Company management navigation group
const COMPANY_MANAGEMENT_GROUP: NavigationGroup = {
  id: "company_management",
  name: "Company Management",
  icon: "🏢",
  description: "Company settings and user management",
  order: 6,
  collapsible: true,
  defaultExpanded: false,
  items: [
    {
      id: "companies",
      name: "Company",
      href: "/companies",
      icon: "🏢",
      description: "Company profile and settings",
      requiredFeatures: [FEATURE_PERMISSIONS.COMPANY_READ],
      requireAll: false,
      order: 1,
      showLock: true, // Show lock when limit reached
    },
    {
      id: "company_users",
      name: "Users",
      href: "/company-users",
      icon: "👨‍💼",
      description: "Manage company team members",
      requiredFeatures: [FEATURE_PERMISSIONS.USERS_READ],
      requireAll: false,
      order: 2,
      showLock: true, // Show lock when limit reached
    },
    {
      id: "company_catalogue",
      name: "Catalogue",
      href: "/catalogue",
      icon: "👨‍💼",
      description: "Manage company catalogue",
      requiredFeatures: [FEATURE_PERMISSIONS.COMPANY_CATALOGUE],
      requireAll: false,
      order: 3,
      showLock: true,
    },
    {
      id: "bookings",
      name: "Bookings",
      href: "/bookings",
      icon: "📅",
      description: "Manage unit reservations and booking configuration",
      requiredFeatures: [],
      requireAll: false,
      badge: "Beta",
      order: 3.5,
      activeFor: ["/bookings/list", "/bookings/configuration"],
    },
    {
      id: "broadcasts",
      name: "Broadcasts",
      href: "/broadcasts",
      icon: "megaphone",
      description: "Send broadcast messages to tenants",
      requiredFeatures: [FEATURE_PERMISSIONS.BROADCAST_WRITE],
      requireAll: false,
      order: 4,
      showLock: true,
      // isNew: true,
    },
  ],
};

// System administration navigation group
const ADMIN_GROUP: NavigationGroup = {
  id: "administration",
  name: "Administration",
  icon: "⚙️",
  description: "System administration and settings",
  order: 7,
  collapsible: true,
  defaultExpanded: false,
  items: [
    {
      id: "admin",
      name: "Admin Management",
      href: "/admin",
      icon: "⚙️",
      description: "System administration panel",
      requiredFeatures: [
        FEATURE_PERMISSIONS.ADMIN_READ,
        FEATURE_PERMISSIONS.ADMIN_WRITE,
      ],
      requireAll: false,
      badge: "Admin",
      order: 1,
    },
    {
      id: "plans",
      name: "Plans & Pricing",
      href: "/plans",
      icon: "💎",
      description: "Manage subscription plans and pricing",
      requiredFeatures: [
        FEATURE_PERMISSIONS.ADMIN_READ,
        FEATURE_PERMISSIONS.ADMIN_WRITE,
      ],
      requireAll: true,
      badge: "Admin",
      order: 2,
    },
    {
      id: "bulk_onboarding",
      name: "Bulk Onboarding",
      href: "/bulk-onboarding",
      icon: "📥",
      description: "Bulk import properties, units, and tenants via CSV",
      requiredFeatures: [
        FEATURE_PERMISSIONS.ADMIN_READ,
        FEATURE_PERMISSIONS.ADMIN_WRITE,
      ],
      requireAll: true,
      badge: "Admin",
      order: 2.5,
    },
    {
      id: "notifications",
      name: "Notifications",
      href: "/notifications",
      icon: "🔔",
      description: "Notification settings and history",
      requiredFeatures: [FEATURE_PERMISSIONS.NOTIFICATIONS_READ],
      requireAll: false,
      showLock: true,
      hideFromSidebar: true, // Hidden from sidebar, accessible via header bell icon
      order: 3,
    },
    {
      id: "notification_logs",
      name: "Notification Logs",
      href: "/notification-logs",
      icon: "📋",
      description: "System notification logs",
      requiredFeatures: [
        FEATURE_PERMISSIONS.NOTIFICATIONS_READ,
        FEATURE_PERMISSIONS.ADMIN_READ,
      ],
      requireAll: true,
      badge: "Admin",
      order: 4,
    },
    {
      id: "auth_logs",
      name: "Auth Logs",
      href: "/auth-logs",
      icon: "🧾",
      description: "Login and signup activity across products",
      requiredFeatures: [FEATURE_PERMISSIONS.ADMIN_READ],
      requireAll: false,
      badge: "Admin",
      order: 4.2,
    },
    {
      id: "test_notifications",
      name: "Test Notifications",
      href: "/test-notifications",
      icon: "🧪",
      description: "Test notification system",
      requiredFeatures: [FEATURE_PERMISSIONS.ADMIN_READ],
      requireAll: false,
      badge: "Admin",
      order: 5,
    },
    {
      id: "leads",
      name: "Leads",
      href: "/leads",
      icon: "leads",
      description: "View and manage incoming leads",
      requiredFeatures: [FEATURE_PERMISSIONS.ADMIN_READ],
      requireAll: false,
      badge: "Admin",
      order: 6,
    },
  ],
};

// All navigation groups in order
export const NAVIGATION_GROUPS: NavigationGroup[] = [
  PROPERTY_MANAGEMENT_GROUP,
  TENANT_MANAGEMENT_GROUP,
  FINANCIAL_MANAGEMENT_GROUP,
  MAINTENANCE_GROUP,
  ANALYTICS_GROUP,
  COMPANY_MANAGEMENT_GROUP,
  ADMIN_GROUP,
].sort((a, b) => a.order - b.order);

// Quick access items (always visible in a separate section)
export const QUICK_ACCESS_ITEMS: NavigationItem[] = [
  // {
  //   id: "chat",
  //   name: "Chat",
  //   href: "/chat",
  //   icon: "💬",
  //   description: "Team communication",
  //   requiredFeatures: [], // Always visible if chat is enabled
  //   requireAll: false,
  //   order: 1,
  // },
];
